import {useTranslation} from 'react-i18next';
import type {PiStatusRow} from '@hisense/shared';
import {usePiStatus} from '@/api/hooks';
import {PageBody} from '@/components/ui/PageHeader';
import {Panel} from '@/components/ui/Panel';
import {Badge} from '@/components/ui/Badge';
import {LoadingState,ErrorState} from '@/components/ui/States';
import {TableShell,Td,Th,Tr} from '@/components/ui/Table';
import {formatDate} from '@/domain/datetime';
import {usePageCrumbs} from '@/components/shell/breadcrumb';
import {ExportButton} from '@/components/domain/ExportButton';

/**
 * Tình trạng PI — dành cho nhân viên KHÔNG được xem giá.
 *
 * Cùng cách rà soát với trang lợi nhuận (thiếu vốn, lệch đơn vị, PO nháp),
 * nhưng không có một con số tiền nào: doanh thu, vốn, lãi và đơn giá không có
 * trong phản hồi API chứ không phải chỉ bị ẩn đi trên màn hình.
 *
 * Số PI để dạng chữ, KHÔNG bấm sang trang PI: trang đó hiện giá, mở ra là phá
 * đúng thứ trang này bảo vệ.
 */
function StatusRow({row}:{row:PiStatusRow}){
 const {t}=useTranslation();
 return <Tr>
  <Td className="font-mono">{row.piNo}</Td>
  <Td>{row.customerName??'—'}</Td>
  <Td>{row.date?formatDate(row.date):'—'}</Td>
  <Td className="font-mono tabular">{row.quantities.length?row.quantities.map(q=>`${q.quantity} ${q.unit}`).join(' · '):'—'}</Td>
  <Td>{row.hasInvoice?t('piStatus.invoiced'):t('piStatus.notInvoiced')}</Td>
  <Td>{t(`piStatus.coverage.${row.costStatus}`)}</Td>
  <Td>{row.issues.length===0
   ? <span className="text-ink-faint">{t('piStatus.noIssue')}</span>
   : <ul className="flex flex-wrap gap-1">{row.issues.map(i=><li key={i}><Badge tone="warn">{t(`piStatus.issues.${i}`,{defaultValue:i})}</Badge></li>)}</ul>}</Td>
 </Tr>;
}

export function PiStatusPage(){
 const {t}=useTranslation();usePageCrumbs([{label:t('piStatus.title')}]);
 const q=usePiStatus();
 return <PageBody>
  <div className="flex flex-wrap items-start justify-between gap-3">
   <div>
    <h1 className="text-xl font-semibold text-ink">{t('piStatus.title')}</h1>
    <p className="mt-1 max-w-3xl text-sm text-ink-soft">{t('piStatus.intro')}</p>
   </div>
   {/*
     Báo cáo này nạp một lần, không phân trang, nên chỉ có một phạm vi.
     KHÔNG có cột tiền nào — trang này dành cho nhân viên không được xem giá, và
     doanh thu/vốn/lãi vốn không nằm trong phản hồi API chứ không phải chỉ bị
     ẩn trên màn hình. Tệp xuất phải giữ đúng ranh giới đó.
   */}
   <ExportButton<PiStatusRow>
    name="pi-status"
    title={t('piStatus.title')}
    rows={q.data?.rows??[]}
    disabled={q.isFetching}
    errorKey="piStatus.error"
    rowKey={r=>r.piId}
    columns={[
     {header:t('piStatus.piNo'),value:r=>r.piNo},
     {header:t('piStatus.customer'),value:r=>r.customerName},
     {header:t('piStatus.date'),value:r=>r.date?formatDate(r.date):null},
     {header:t('common.currency'),value:r=>r.currency},
     {header:t('common.items'),value:r=>r.itemCount},
     // Số lượng gộp theo đơn vị: "1200 pieces · 30 sets". Một PI có thể có
     // nhiều đơn vị nên không cộng thành một con số được.
     {header:t('piStatus.quantity'),value:r=>r.quantities.map(x=>`${x.quantity} ${x.unit}`).join(' · ')},
     {header:t('piStatus.invoice'),value:r=>t(r.hasInvoice?'piStatus.invoiced':'piStatus.notInvoiced')},
     {header:t('piStatus.costCoverage'),value:r=>t(`piStatus.coverage.${r.costStatus}`)},
     {header:t('piStatus.needsAttention'),value:r=>r.issues.map(i=>t(`piStatus.issues.${i}`,{defaultValue:i})).join(' · ')},
    ]}
   />
  </div>
  {q.isLoading?<LoadingState/>:q.error||!q.data?<ErrorState message={t('piStatus.error')} onRetry={()=>void q.refetch()}/>:
   q.data.rows.length===0?<Panel className="mt-4 p-6"><p className="text-sm text-ink-soft">{t('piStatus.empty')}</p></Panel>:
   <div className="mt-4">
    <TableShell>
     <thead><Tr>
      <Th>{t('piStatus.piNo')}</Th><Th>{t('piStatus.customer')}</Th><Th>{t('piStatus.date')}</Th>
      <Th>{t('piStatus.quantity')}</Th><Th>{t('piStatus.invoice')}</Th>
      <Th>{t('piStatus.costCoverage')}</Th><Th>{t('piStatus.needsAttention')}</Th>
     </Tr></thead>
     <tbody>{q.data.rows.map(r=><StatusRow key={r.piId} row={r}/>)}</tbody>
    </TableShell>
   </div>}
 </PageBody>;
}
